import { AvailabilityZone } from '@core/service/availability-zone.handler';
import {Component, OnInit, ViewEncapsulation, OnDestroy, ViewChild, ElementRef, NgZone} from '@angular/core';
import {Subject, tap} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {AvailabilityZoneHandler} from '@core/service/availability-zone.handler';
import {AZLayer, AZGroup, FlatAZItem} from '@core/model/availability-zone.interface';
import {ProvidersSelector} from '@core/ngrx/selectors/providers.selector';
import {Provider} from '@core/model/providers';
import * as providersActions from '../../core/ngrx/actions/provider.actions';
import * as datacenterActions from '../../core/ngrx/actions/datacenter.actions';
import * as availabilityZoneActions from '../../core/ngrx/actions/availability-zone.actions';
import { DatacenterV2 } from 'src/app/core/model/datacenter-v2';
import { PRODUCT_TYPES } from 'src/app/core/model/datacenter';

@Component({
  selector: 'app-availability-zones-sidebar',
  templateUrl: './availability-zones-sidebar.component.html',
  styleUrls: ['./availability-zones-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AvailabilityZonesSidebarComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef;
  groupedLayers: AZGroup[] = [];
  flatItems: FlatAZItem[] = [];
  public changingColorProvider: Provider = null;
  public changingColor: string = null;
  public currentFilter: string = "";
  public isSearching: boolean = false;
  public isLoadingAZ: boolean = true;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private providers: Provider[] = [];

  constructor(
    private availabilityZoneHandler: AvailabilityZoneHandler,
    private providersSelector: ProvidersSelector,
    private store: Store,
    public actions$: Actions,
    private ngZone: NgZone
  ) {
  }

  ngOnInit() {
    this.subscribeToProviderActions();
    this.subscribeToDatacenterActions();
    this.subscribeToAvailabilityZoneActions();
  }

  private subscribeToDatacenterActions(): void {
    // Subscription for when datacenters are successfully obtained
    this.actions$.pipe(
      ofType(datacenterActions.getDatacentersSuccess),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          if (action.lastBatch) {
            this.loadAvailabilityZones();
            this.resetFilter();
          }
        });
      })
    ).subscribe();

    // Subscription for when a datacenter is modified
    this.actions$.pipe(
      ofType(datacenterActions.datacenterModified),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          const modifiedDatacenter = action.datacenter;
          this.modifiedOrCreateAZInTree(modifiedDatacenter);
        });
      })
    ).subscribe();

    // Subscription for when a datacenter is removed
    this.actions$.pipe(
      ofType(datacenterActions.removeDatacenter),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          const datacenterId = action?.datacenter?.id || action?.datacenter?.auxId;
          this.availabilityZoneHandler.removeAZFromMap(action?.datacenter?.id);
          this.removeAZInTree(datacenterId);
        });
      })
    ).subscribe();
  }

  private subscribeToProviderActions(): void {
    // Subscription to get the list of providers
    this.providersSelector.getProviders().pipe(
      takeUntil(this.destroy$),
      tap((providers: Provider[]) => this.providers = providers)
    ).subscribe();

    // Subscription for when a provider color changes
    this.actions$.pipe(
      ofType(providersActions.providerColorChanged),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          const provider = action.provider;
          this.availabilityZoneHandler.onProviderColorChanged(provider?.id || provider?.auxId, provider?.color);
          this.updateProviderColorInTree(provider);
        });
      })
    ).subscribe();

    // Subscription for when providers are modified
    this.actions$.pipe(
      ofType(providersActions.providersModified),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          const provider = action.provider;
          this.availabilityZoneHandler.onProviderColorChanged(provider?.id || provider?.auxId, provider?.color);
          this.updateProviderColorInTree(provider);
        });
      })
    ).subscribe();
  }

  private subscribeToAvailabilityZoneActions(): void {
    // Subscription for when availability zone visibility changes
    this.actions$.pipe(
      ofType(availabilityZoneActions.toggleAZVisibility),
      takeUntil(this.destroy$),
      tap((action) => {
        this.ngZone.run(() => {
          this.updateAZVisibilityInTree(action.azId, action.isVisible);
        });
      })
    ).subscribe();
  }

  private modifiedOrCreateAZInTree(datacenter: DatacenterV2): void {
    if (!datacenter) return;

    const isNewDatacenter = this.isNewDatacenter(datacenter);
    const hasAvailabilityZone = datacenter.productType?.includes(PRODUCT_TYPES.AVAILABILITY_ZONE) &&
                               datacenter.availabilityZoneId;

    if (isNewDatacenter && hasAvailabilityZone) {
      this.addDatacenterToTree(datacenter);
    } else if (!isNewDatacenter) {
      if (hasAvailabilityZone) {
        this.updateDatacenterInTree(datacenter);
      } else {
        this.removeAZInTree(datacenter.id || datacenter.auxId);
      }
    }
  }

  private isNewDatacenter(datacenter: any): boolean {
    const exists = this.groupedLayers.some(group =>
      group.layers.some(layer =>
        layer.id === datacenter.id ||
        (datacenter.auxId && layer.id === datacenter.auxId) ||
        // Handle the case where datacenter has both auxId and id (during save transition)
        (datacenter.auxId && datacenter.id && (layer.id === datacenter.auxId || layer.id === datacenter.id))
      )
    );
    return !exists;
  }

  private addDatacenterToTree(datacenter: any): void {
    const newLayer: AZLayer = {
      id: datacenter.id || datacenter.auxId,
      provider: datacenter.provider,
      azId: datacenter.availabilityZoneId,
      building: datacenter.building,
      isVisible: false,
      color: this.getProviderColor(datacenter.provider)
    };

    const groupKey = `${datacenter.provider} / ${datacenter.availabilityZoneId}`;
    let group = this.groupedLayers.find(g => g.azId === groupKey);

    if (!group) {
      group = {
        azId: groupKey,
        providerId: datacenter.providerId,
        layers: []
      };
      this.groupedLayers.push(group);
    }

    group.layers.push(newLayer);

    group.layers.sort((a, b) =>
      a.building.toLowerCase().localeCompare(b.building.toLowerCase())
    );

    this.groupedLayers.sort((a, b) =>
      a.azId.toLowerCase().localeCompare(b.azId.toLowerCase())
    );

    this.flattenItemsForVirtualScroll();
  }

  private updateDatacenterInTree(datacenter: any): void {
    let hasChanges = false;

    this.groupedLayers.forEach(group => {
      group.layers.forEach(layer => {
        if (layer.id === datacenter.id ||
            (datacenter.auxId && layer.id === datacenter.auxId)) {
          // Update the layer ID if this datacenter was saved (auxId -> id transition)
          if (datacenter.auxId && layer.id === datacenter.auxId && datacenter.id) {
            layer.id = datacenter.id;
          }
          layer.provider = datacenter.provider;
          layer.azId = datacenter.availabilityZoneId;
          layer.building = datacenter.building;
          layer.color = datacenter.color || this.getProviderColor(datacenter.provider);
          hasChanges = true;
        }
      });
    });

    if (hasChanges) {
      this.flattenItemsForVirtualScroll();
    }
  }

  private getProviderColor(providerName: string): string {
    const provider = this.providers.find(p => p.name === providerName);
    return provider?.color;
  }

  private loadAvailabilityZones() {
    const availabilityZones: AvailabilityZone[] = this.availabilityZoneHandler?.getAvailabilityZones();
    this.generateGroupedLayersByAZ(availabilityZones);
  }

  private generateGroupedLayersByAZ(availabilityZones) {
    this.groupedLayers = []; // Reset groups
    const processedAZs = this.availabilityZoneHandler.applyColorsToDatacenters(availabilityZones, this.providers);
    processedAZs.forEach(dc => {
      const key = `${dc.provider} / ${dc.availabilityZoneId}`;
      let group = this.groupedLayers.find(group => group.azId === key);

      if (!group) {
        group = {azId: key, layers: [], providerId: dc.providerId};
        this.groupedLayers.push(group);
      }

      const buildings = group.layers;
      if (!buildings.find(b => b.building === dc.building && b.id === dc.id)) {
        const currentVisibility = this.availabilityZoneHandler.isAZVisibleInSource(dc.id);
        buildings.push({
          id: dc.id,
          provider: dc.provider,
          azId: dc.availabilityZoneId,
          building: dc.building,
          isVisible: currentVisibility,
          color: dc.color
        });
      }
    });

    // Sort groups and layers
    this.groupedLayers = this.groupedLayers
      .filter(group => group.layers.length > 0)
      .sort((a, b) => a.azId.toLowerCase().localeCompare(b.azId.toLowerCase()));

    this.groupedLayers.forEach(group => {
      group.layers = group.layers.sort((a, b) =>
        a.building.toLowerCase().localeCompare(b.building.toLowerCase())
      );
    });

    this.flattenItemsForVirtualScroll();
  }

  private flattenItemsForVirtualScroll(): void {
    this.flatItems = [];

    this.groupedLayers.forEach(group => {
      this.flatItems.push({
        type: 'group',
        azId: group.azId,
        providerId: group.providerId,
        isExpanded: true
      });

      group.layers.forEach(layer => {
        this.flatItems.push({
          type: 'layer',
          layer: layer
        });
      });
    });
  }

  public toggleGroupExpansion(groupIndex: number): void {
    const groupItem = this.flatItems[groupIndex];
    if (groupItem.type === 'group') {
      groupItem.isExpanded = !groupItem.isExpanded;
      this.rebuildFlatItems();
    }
  }

  private rebuildFlatItems(): void {
    this.flatItems = [];

    this.groupedLayers.forEach(group => {
      const groupItem: FlatAZItem = {
        type: 'group',
        azId: group.azId,
        providerId: group.providerId,
        isExpanded: true
      };

      this.flatItems.push(groupItem);

      if (groupItem.isExpanded) {
        group.layers.forEach(layer => {
          this.flatItems.push({
            type: 'layer',
            layer: layer
          });
        });
      }
    });
  }

  public toggleAZVisibility(layer: AZLayer): void {

    this.availabilityZoneHandler.toggleAZVisibilityInMap(layer.id, !layer.isVisible);
  }

  public onColorChanged(event: any, layer: AZLayer): void {
    this.changingColorProvider = this.providers.find(p => p.name === layer.provider);
    this.changingColor = event.value;
  }

  public onHideColorPicker(): void {
    if (this.changingColorProvider && this.changingColor) {
      const updatedProvider = {
        ...this.changingColorProvider,
        color: this.changingColor
      };

      this.store.dispatch(providersActions.providerColorChanged({
        provider: updatedProvider
      }));

      this.changingColorProvider = null;
      this.changingColor = null;
    }
  }

  public filter(table: any, event: any): void {
    const filterValue = event.target.value.trim();
    this.currentFilter = filterValue;
    this.isSearching = filterValue && filterValue.length > 0;

    if (this.isSearching) {
      this.groupedLayers = this.groupedLayers.filter(group =>
        group.azId.toLowerCase().includes(filterValue.toLowerCase()) ||
        group.layers.some(layer =>
          layer.provider.toLowerCase().includes(filterValue.toLowerCase()) ||
          layer.building.toLowerCase().includes(filterValue.toLowerCase()) ||
          layer.id.toLowerCase().includes(filterValue.toLowerCase())
        )
      );
      this.flattenItemsForVirtualScroll();
    } else {
      this.loadAvailabilityZones();
    }
  }

  private resetFilter(): void {
    this.currentFilter = null;
    this.isSearching = false;
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  private removeAZInTree(datacenterId: string): void {
    if (!datacenterId) {
      return;
    }

    let hasChanges = false;

    this.groupedLayers.forEach(group => {
      const initialLength = group.layers.length;
      group.layers = group.layers.filter(layer => layer.id !== datacenterId);

      if (group.layers.length !== initialLength) {
        hasChanges = true;
      }
    });

    const initialGroupsLength = this.groupedLayers.length;
    this.groupedLayers = this.groupedLayers.filter(group => group.layers.length > 0);

    if (this.groupedLayers.length !== initialGroupsLength) {
      hasChanges = true;
    }

    if (hasChanges) {
      this.flattenItemsForVirtualScroll();
    }
  }

  private updateAZVisibilityInTree(azId: string, isVisible: boolean): void {
    if (!azId) {
      return;
    }

    let hasChanges = false;

    this.groupedLayers.forEach(group => {
      group.layers.forEach(layer => {
        if (layer.id === azId) {
          layer.isVisible = isVisible;
          hasChanges = true;
        }
      });
    });

    if (hasChanges) {
      this.flattenItemsForVirtualScroll();
    }
  }

  private updateProviderColorInTree(provider: Provider): void {
    if (!provider || !provider.color) {
      return;
    }

    let hasChanges = false;

    this.groupedLayers.forEach(group => {
      const belongsToProvider = group.providerId === provider.id ||
                               group.providerId === provider.auxId ||
                               group.layers.some(layer => layer.provider === provider.name);
      if (belongsToProvider) {
        group.layers.forEach(layer => {
          if (layer.provider === provider.name) {
            layer.color = provider.color;
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      this.flattenItemsForVirtualScroll();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
